"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useMediaUrlWithState = useMediaUrlWithState;
const react_1 = require("react");
const OrganizationContext_1 = require("../context/OrganizationContext");
const useFetchSignedUrlInfoWithSocket_1 = require("./useFetchSignedUrlInfoWithSocket");
const AUTO_RETRY_DELAY_MS = 1000;
const MAX_AUTO_ATTEMPTS = 2;
function useMediaUrlWithState(url) {
    const { selectedOrgUser } = (0, react_1.useContext)(OrganizationContext_1.OrganizationContext);
    const [data, setData] = (0, react_1.useState)(undefined);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(false);
    const attemptRef = (0, react_1.useRef)(0);
    const retryTimeoutRef = (0, react_1.useRef)(null);
    const mountedRef = (0, react_1.useRef)(true);
    const activeUrlRef = (0, react_1.useRef)(undefined);
    (0, react_1.useEffect)(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }
        };
    }, []);
    const requestForUrl = (0, react_1.useCallback)((targetUrl, fetchFresh) => {
        if (!selectedOrgUser)
            return;
        setLoading(true);
        setError(false);
        selectedOrgUser.assetManager.getSignedUrlInfoWithSocket(targetUrl, (info) => {
            if (!mountedRef.current)
                return;
            // Ignore stale responses if url changed mid-flight.
            if (activeUrlRef.current !== targetUrl)
                return;
            if (info === null || info === void 0 ? void 0 : info.signedUrl) {
                setData(info);
                setLoading(false);
                setError(false);
                attemptRef.current = 0;
                return;
            }
            // Failed. Auto-retry once before settling into error so that
            // transient backend issues recover without user interaction.
            // The retry passes fetchFresh=true so it bypasses the manager's
            // negative cache (which we just populated) and actually re-hits
            // the backend.
            attemptRef.current += 1;
            if (attemptRef.current < MAX_AUTO_ATTEMPTS) {
                retryTimeoutRef.current = setTimeout(() => {
                    if (!mountedRef.current)
                        return;
                    if (activeUrlRef.current !== targetUrl)
                        return;
                    requestForUrl(targetUrl, true);
                }, AUTO_RETRY_DELAY_MS);
                return;
            }
            setLoading(false);
            setError(true);
            attemptRef.current = 0;
        }, fetchFresh);
    }, [selectedOrgUser]);
    (0, react_1.useEffect)(() => {
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
        attemptRef.current = 0;
        activeUrlRef.current = url;
        setError(false);
        if (!url) {
            setData(undefined);
            setLoading(false);
            return;
        }
        if ((0, useFetchSignedUrlInfoWithSocket_1.isBlobImage)(url)) {
            setData({ expiry: 0, signedUrl: url });
            setLoading(false);
            return;
        }
        setData(undefined);
        // Short-circuit on recent failure: if the manager already knows this URL
        // failed recently, show the error state immediately rather than going
        // through a fake loading → auto-retry → error sequence the user can see.
        if (selectedOrgUser === null || selectedOrgUser === void 0 ? void 0 : selectedOrgUser.assetManager.hasRecentError(url)) {
            setLoading(false);
            setError(true);
            return;
        }
        requestForUrl(url, false);
    }, [url, requestForUrl, selectedOrgUser]);
    const retry = (0, react_1.useCallback)(() => {
        if (!url || (0, useFetchSignedUrlInfoWithSocket_1.isBlobImage)(url))
            return;
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
        attemptRef.current = 0;
        // User-initiated retry: bypass both caches and force a fresh fetch.
        requestForUrl(url, true);
    }, [url, requestForUrl]);
    return { data, loading, error, retry };
}
